import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Styled card wrapper for each section block
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-[var(--color-secondary-muted)] rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => (
  <p
    id={id}
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const TokenLaunchStrategy = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto text-center space-y-4"
    >
      <h2 className="text-3xl font-bold text-primary-dark">
        Safe and Transparent Token on Uniswap
      </h2>
      <Paragraph
        id="roadmap-Presale"
        className="text-gray-700 max-w-6xl mx-auto"
      >
        <strong>Project Context:</strong> The following outlines the
        step-by-step plan to launch the NAU, NAUY, and NAUN tokens on Uniswap,
        using PinkSale as the launchpad to ensure a secure, transparent, and
        community-driven token offering with no risk of rug pulls.
      </Paragraph>
    </motion.div>

    {/* Two-column layout */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      {/* Left Column */}
      <div className="flex-1 flex flex-col space-y-6">
        {/* Section 1 */}
        <SectionCard title="1. Objectives of the Launch">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Raise 300,000 USDC in total funding through a fair token presale.
            </li>
            <li>
              Immediately list tokens on Uniswap with sufficient liquidity.
            </li>
            <li>Lock liquidity to build trust and prevent rug pulls.</li>
            <li>Create a strong, loyal community around each token.</li>
          </ul>
        </SectionCard>

        {/* Section 2 */}
        <SectionCard title="2. Launchpad Configuration">
          <div className="overflow-x-auto">
            <table className="min-w-full text-base text-gray-800">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">
                    Parameter
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">Value</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Token Price", "0.0024 USDC"],
                  ["Soft Cap", "75,000 USDC"],
                  ["Hard Cap", "300,000 USDC"],
                  ["Min Contribution", "100 USDC"],
                  ["Max Contribution", "10,000 USDC"],
                  ["Sale Type", "Public"],
                  ["Vesting", "50% at TGE + 2-month linear vest"],
                  ["Liquidity %", "80% of USDC raised"],
                  ["Liquidity Lock", "12 months"],
                  ["Launchpad Fee", "5% or as specified by Launchpad"],
                ].map(([param, value]) => (
                  <tr className="border-t" key={param}>
                    <td className="px-4 py-2">{param}</td>
                    <td className="px-4 py-2">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Section 3 */}
        <SectionCard title="3. Uniswap Listing Plan">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Automatically create a Uniswap pool using raised USDC and
              allocated tokens after the presale.
            </li>
            <li>
              Add 80% of raised funds as liquidity.
              {/* based on PinkSale
              configuration. */}
            </li>
            <li>
              Lock liquidity tokens via PinkLock or Unicrypt for 12 months.
            </li>
            <li>
              Announce the Uniswap pair contract address publicly to prevent
              impersonation.
            </li>
          </ul>
        </SectionCard>
      </div>

      {/* Right Column */}
      <div className="flex-1 flex flex-col space-y-6">
        {/* Section 4 */}
        <SectionCard title="4. Anti-Rug Measures">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Liquidity Lock: LP tokens will be locked using a verifiable smart
              contract.
            </li>
            <li>
              No Mint Function: Tokens will have a fixed supply with no further
              minting allowed.
            </li>
            <li>
              Audit + Verification: Smart contracts will be audited and source
              code verified on BaseScan.
            </li>
            <li>
              Team Vesting: Team wallets will be subject to long vesting
              periods.
            </li>
          </ul>
        </SectionCard>

        {/* Section 5 */}
        <SectionCard title="5. Post-Launch Commitments">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Community Engagement: Active Telegram/Discord, regular AMAs.
            </li>
            <li>
              Staking Launch: Staking contracts for NAU, NAUY, NAUY will be
              launched.
            </li>
            <li>Governance: Voting mechanisms for NAUY and NAUN initiated.</li>
            <li>
              Continuous Updates: Token holders will receive quarterly updates.
            </li>
          </ul>
        </SectionCard>

        {/* Section 6 */}
        <SectionCard title="6. Summary">
          <Paragraph>
            By launching via a launchpad with listing on Uniswap and a locked
            liquidity plan, the NAU, NAUY, and NAUN tokens are structured for
            fair distribution, transparent market price discovery, and long-term
            community confidence. This approach protects investors and sets a
            strong foundation for ecosystem growth.
          </Paragraph>
        </SectionCard>
      </div>
    </div>
  </div>
);

export default TokenLaunchStrategy;
